import { isPaymentAlreadyInitiatedError } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { type ErrorComponentProps, Link, useRouterState } from '@tanstack/react-router'
import { AlertCircle, CreditCard, Plane, RefreshCw, Search } from 'lucide-react'

import { ClientErrorState } from '@/components/errors/client-error-state'
import { type ClientErrorType, resolveClientErrorType } from '@/lib/client-error'
import { useRestartFlightSearch } from '@/lib/restart-flight-search'

/**
 * Frontière d'erreur du tunnel de réservation (`routes/_booking.tsx`).
 *
 * Elle attrape ce que les trois étapes lui envoient par `throwOnError` — la
 * session, le tarif, l'assurance — et rend le catalogue partagé sous le logo de
 * l'agence. Les formulaires, eux, gardent leurs erreurs en ligne : perdre une
 * saisie passagers pour une panne prestataire serait pire que la panne.
 */
export function BookingError({ error, reset }: ErrorComponentProps) {
  const routerState = useRouterState()
  const restartFlightSearch = useRestartFlightSearch()
  const sessionId = (routerState.location.search as { session?: string }).session

  if (isPaymentAlreadyInitiatedError(error) && sessionId) {
    return <PaymentAlreadyInitiated sessionId={sessionId} />
  }

  const type = resolveClientErrorType(error)
  // Sorti dans une variable plutôt qu'inline : `jsx-curly-brace-presence` retire
  // les accolades d'un élément passé directement en attribut, ce qui ne compile pas.
  const actions = (
    <BookingErrorActions onReset={reset} onRestartSearch={restartFlightSearch} type={type} />
  )

  return (
    <BookingErrorFrame>
      <ClientErrorState actions={actions} error={error} type={type} variant="page" />
    </BookingErrorFrame>
  )
}

/**
 * Les sorties possibles, dans l'ordre où le client doit les envisager.
 *
 * Deux règles s'y croisent. « Réessayer » ne sert à rien sur une session morte —
 * le cache d'offres a disparu côté serveur, c'est une recherche neuve qu'il faut
 * — et il est dangereux sur un dossier au sort indéterminé, où relancer créerait
 * un doublon chez la compagnie ; ce cas-là n'a que le téléphone de l'agence, que
 * `ClientErrorState` affiche lui-même.
 *
 * « Relancer la recherche » passe par `useRestartFlightSearch` plutôt que par un
 * lien vers `/recherche` : le lien seul ne purgeait aucun cache et ramenait
 * l'utilisateur sur les mêmes offres périmées.
 */
function BookingErrorActions({
  onReset,
  onRestartSearch,
  type,
}: {
  onReset: () => void
  onRestartSearch: () => void
  type: ClientErrorType
}) {
  const isStale = type === 'price-changed' || type === 'session-expired'
  const canRetry = !isStale && type !== 'booking-uncertain'

  return (
    <>
      {canRetry && (
        <Button className="bg-brand-600 hover:bg-brand-700" onClick={onReset}>
          <RefreshCw className="size-4" />
          Réessayer
        </Button>
      )}
      <Button
        className={isStale ? 'bg-brand-600 hover:bg-brand-700' : undefined}
        onClick={onRestartSearch}
        variant={isStale ? 'default' : 'outline'}
      >
        <Search className="size-4" />
        Relancer la recherche
      </Button>
    </>
  )
}

/** Le logo et le filet, communs à tous les écrans d'erreur du tunnel. */
function BookingErrorFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center text-center">
        <img alt="Massilia Voyages" className="h-10" src="/logos/massilia-logo.png" />
        <div className="mt-8 h-px w-16 bg-gray-light-200" />
        {children}
      </div>
    </div>
  )
}

/**
 * Cas à part : le paiement est déjà lancé, il n'y a pas d'erreur à expliquer,
 * seulement un parcours à reprendre là où il s'est arrêté.
 */
function PaymentAlreadyInitiated({ sessionId }: { sessionId: string }) {
  return (
    <BookingErrorFrame>
      <div className="mt-8 flex size-16 items-center justify-center rounded-full bg-warning-subtle">
        <AlertCircle className="size-8 text-warning-foreground" />
      </div>
      <h1 className="mt-6 text-xl font-semibold text-gray-light-900">Paiement déjà initié</h1>
      <p className="mt-2 text-sm leading-relaxed text-gray-light-600">
        Le paiement a déjà été initié pour cette réservation. Il n&apos;est plus possible de
        modifier les informations concernant les passagers ou l&apos;assurance. Reprenez le paiement
        là où vous l&apos;aviez laissé.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Button asChild variant="outline">
          <Link to="/recherche">
            <Plane className="size-4" />
            Retour à la recherche
          </Link>
        </Button>
        <Button asChild className="bg-brand-600 hover:bg-brand-700">
          <Link search={{ session: sessionId }} to="/paiement">
            <CreditCard className="size-4" />
            Reprendre le paiement
          </Link>
        </Button>
      </div>
    </BookingErrorFrame>
  )
}
